/**
 * The client directory behind the lodge's client picker (13-24, D-13-L-7, D-13-24-1 … -4).
 *
 * Two canonical client spines, read by delegated Graph as the signed-in person (no app permission):
 *   - **SAH**: `HarmonyClients` on the Support at Home client management site;
 *   - **NDIS**: `Clients` on the NDIS client management site.
 * Both are resolved at runtime by site path + list title, the way `useGraphClient` resolves the QMS lists, so no
 * list GUID is committed. The columns were measured 2026-10-12 (columns only, GET, no item values read):
 *   - SAH: `Title` (the client's name), `Preferred_x0020_Name`, `Status_x002a_` (Choice).
 *   - NDIS: `FirstName`, `LastName`, `PreferredName`, `AgreementStatus` (Choice). **`Title` on this list holds the
 *     NDIS number**, and `NDISNumber` is the number itself, so neither is ever selected or shown (D-13-24-2).
 * Neither list carries a ShiftCare client id, so `RecipientClientId` is `SAH-<SP Id>` / `NDIS-<SP Id>` (D-13-24-1).
 *
 * No list has an indexed name column, so there is no server-side `startswith`: each list is read once per page load
 * (`$expand=fields($select=…)`, the selection INSIDE the expand, paged on `@odata.nextLink`) and kept **in memory
 * only**, never in localStorage or sessionStorage: client names stay out of storage (D-13-L-7). `searchClients`
 * filters that copy. A 403 on one list makes that list `unavailable` and leaves the other working; any other failure
 * is reported the same way and is not cached, so the next search tries again.
 *
 * Status (D-13-24-3): a client is active only when the status column holds the live "active" value verbatim, which
 * on SAH is `Active,` (the trailing comma is in the live choice) and on NDIS `Active`. Every other value, and a
 * blank, is not active: listed last and marked with the value itself. Values are compared exactly, never trimmed or
 * normalised.
 */

import type { Client } from '@microsoft/microsoft-graph-client';
import { errorStatus, getListId, listItemEnvelopes, toGraphApiError, withRetry } from './graphClient';
import type { GraphListItemEnvelope, RetryOptions } from './graphClient';

// ---------------------------------------------------------------------------
// The two spines (measured 2026-10-12; no GUIDs, no values)
// ---------------------------------------------------------------------------

export type ClientListKey = 'SAH' | 'NDIS';

interface ClientListSpec {
  /** Graph site path: `<host>:/sites/<name>`. */
  site: string;
  /** The list's display title on that site. */
  title: string;
  /** The columns read, in `$expand=fields($select=…)`. Never an NDIS number. */
  select: readonly string[];
  /** The status column, compared verbatim. */
  statusColumn: string;
  /** The status value that means "active", verbatim. */
  activeValue: string;
  /** How the status reads in the "not active" note. */
  statusLabel: string;
  /** The list's name for a person, in plain words. */
  label: string;
}

export const CLIENT_LISTS: Readonly<Record<ClientListKey, ClientListSpec>> = {
  SAH: {
    site: 'harmonyhc1.sharepoint.com:/sites/SupportatHomeClientManagement',
    title: 'HarmonyClients',
    select: ['Title', 'Preferred_x0020_Name', 'Status_x002a_'],
    statusColumn: 'Status_x002a_',
    activeValue: 'Active,',
    statusLabel: 'Status',
    label: 'Support at Home',
  },
  NDIS: {
    site: 'harmonyhc1.sharepoint.com:/sites/NDISClientManagement',
    title: 'Clients',
    select: ['FirstName', 'LastName', 'PreferredName', 'AgreementStatus'],
    statusColumn: 'AgreementStatus',
    activeValue: 'Active',
    statusLabel: 'Agreement',
    label: 'NDIS',
  },
};

/** Columns never read from a client list: the NDIS number lives in both (D-13-24-2). */
export const NEVER_SELECTED: Readonly<Record<ClientListKey, readonly string[]>> = {
  SAH: [],
  NDIS: ['Title', 'NDISNumber'],
};

/**
 * Which client lists a `BusinessUnit` (a client stream, D-12.1-14-5) searches. The keys are the live
 * `CLIENT_STREAMS` values; `clientDirectory.test.ts` checks they match. A stream not listed searches nothing.
 */
const STREAM_LISTS: Readonly<Record<string, readonly ClientListKey[]>> = {
  'Support at Home Package': ['SAH'],
  'Brokered or Private': ['SAH'],
  NDIS: ['NDIS'],
  'Both NDIS & Aged Care': ['SAH', 'NDIS'],
};

/** The streams this module maps (for the test against `CLIENT_STREAMS`). */
export const MAPPED_STREAMS: readonly string[] = Object.keys(STREAM_LISTS);

/** The lists a stream searches, in a fixed order (SAH first). */
export function listsForStream(stream: string | null | undefined): readonly ClientListKey[] {
  return (stream && STREAM_LISTS[stream]) || [];
}

/** `RecipientClientId` for a list item (D-13-24-1): `SAH-<SP Id>` / `NDIS-<SP Id>`. */
export function clientIdOf(list: ClientListKey, spId: number | string): string {
  const id = Number(spId);
  if (!Number.isSafeInteger(id) || id < 1) throw new Error(`clientDirectory: not an item id: ${String(spId)}`);
  return `${list}-${id}`;
}

/** Is this status the list's "active" value? Exact comparison, never trimmed (`Active,` keeps its comma). */
export function isActiveStatus(list: ClientListKey, status: string | null | undefined): boolean {
  return typeof status === 'string' && status === CLIENT_LISTS[list].activeValue;
}

// ---------------------------------------------------------------------------
// Entries
// ---------------------------------------------------------------------------

/** One client as the picker shows it. Never carries an NDIS number. */
export interface ClientEntry {
  /** `RecipientClientId`: `SAH-<Id>` / `NDIS-<Id>`. */
  id: string;
  /** What `RecipientName` gets. */
  name: string;
  /** A preferred name, when the list has one that differs. Shown and searched; never written. */
  preferredName?: string;
  list: ClientListKey;
  active: boolean;
  /** The status value verbatim (`null` when blank). */
  status: string | null;
}

export type ClientListProblem = 'forbidden' | 'error';

export interface ClientSearchResult {
  matches: ClientEntry[];
  /** Lists that could not be read: `forbidden` (403, "not available to you") or `error` (try again). */
  unavailable: { list: ClientListKey; reason: ClientListProblem }[];
}

function text(fields: Record<string, unknown>, key: string): string {
  const v = fields[key];
  return typeof v === 'string' ? v.trim() : '';
}

/** A list item → an entry, or `null` when the item has no name to pick by. */
export function entryOf(list: ClientListKey, item: GraphListItemEnvelope): ClientEntry | null {
  const f = item.fields ?? {};
  const spec = CLIENT_LISTS[list];
  let name: string;
  let preferred: string;
  if (list === 'SAH') {
    name = text(f, 'Title');
    preferred = text(f, 'Preferred_x0020_Name');
  } else {
    name = [text(f, 'FirstName'), text(f, 'LastName')].filter(Boolean).join(' ');
    preferred = text(f, 'PreferredName');
  }
  if (!name) name = preferred;
  if (!name) return null;
  const rawStatus = f[spec.statusColumn];
  const status = typeof rawStatus === 'string' && rawStatus !== '' ? rawStatus : null;
  return {
    id: clientIdOf(list, item.id),
    name,
    ...(preferred && preferred.toLowerCase() !== name.toLowerCase() ? { preferredName: preferred } : {}),
    list,
    active: isActiveStatus(list, status),
    status,
  };
}

/** Active first, then by name (en-AU), then by id. */
export function sortEntries(entries: readonly ClientEntry[]): ClientEntry[] {
  return [...entries].sort(
    (a, b) =>
      Number(b.active) - Number(a.active) ||
      a.name.localeCompare(b.name, 'en-AU', { sensitivity: 'base' }) ||
      a.id.localeCompare(b.id),
  );
}

/** Every word of the query appears in the name or the preferred name (case-insensitive). Blank matches all. */
export function matchesQuery(entry: ClientEntry, query: string): boolean {
  const words = query.toLowerCase().split(/\s+/).filter(Boolean);
  if (words.length === 0) return true;
  const hay = `${entry.name} ${entry.preferredName ?? ''}`.toLowerCase();
  return words.every((w) => hay.includes(w));
}

/** The "not active" note, in plain words (`Status: Exited`, `Agreement: Expired`, `Status not recorded`). */
export function inactiveNote(entry: Pick<ClientEntry, 'list' | 'active' | 'status'>): string | null {
  if (entry.active) return null;
  const spec = CLIENT_LISTS[entry.list];
  return entry.status === null ? `${spec.statusLabel} not recorded` : `${spec.statusLabel}: ${entry.status}`;
}

// ---------------------------------------------------------------------------
// Reading (one paged read per list per page load, memory only)
// ---------------------------------------------------------------------------

export interface ClientDirectoryIO {
  client: Client;
  retry?: RetryOptions;
}

type ListLoad = { ok: true; entries: ClientEntry[] } | { ok: false; reason: ClientListProblem };

/** Per Graph client (a sign-in), per list: the load in flight or done. Module memory: gone on reload. */
let loads = new WeakMap<Client, Map<ClientListKey, Promise<ListLoad>>>();

/** Forget every loaded list (tests; a sign-out drops the client, and with it its entries). */
export function clearClientDirectory(): void {
  loads = new WeakMap();
}

/** The Graph site id for a site path (`<host>:/sites/<name>`). */
async function siteIdOf(io: ClientDirectoryIO, sitePath: string): Promise<string> {
  return withRetry(async () => {
    try {
      const site = (await io.client.api(`/sites/${sitePath}`).select('id').get()) as { id?: string };
      if (!site?.id) throw new Error(`no site id for ${sitePath}`);
      return site.id;
    } catch (error) {
      throw toGraphApiError(error, `clientDirectory.site(${sitePath})`);
    }
  }, io.retry);
}

async function readList(io: ClientDirectoryIO, list: ClientListKey): Promise<ListLoad> {
  const spec = CLIENT_LISTS[list];
  try {
    const siteId = await siteIdOf(io, spec.site);
    const listId = await getListId(io.client, siteId, spec.title);
    const items = await listItemEnvelopes(io.client, siteId, listId, { select: spec.select, top: 999, retry: io.retry });
    const entries = items.map((item) => entryOf(list, item)).filter((e): e is ClientEntry => e !== null);
    return { ok: true, entries: sortEntries(entries) };
  } catch (err) {
    return { ok: false, reason: errorStatus(err) === 403 ? 'forbidden' : 'error' };
  }
}

function loadList(io: ClientDirectoryIO, list: ClientListKey): Promise<ListLoad> {
  let perClient = loads.get(io.client);
  if (!perClient) {
    perClient = new Map();
    loads.set(io.client, perClient);
  }
  const cached = perClient.get(list);
  if (cached) return cached;
  const p = readList(io, list).then((r) => {
    // A 403 is an answer for this page load; any other failure is retried on the next search.
    if (!r.ok && r.reason === 'error') perClient.delete(list);
    return r;
  });
  perClient.set(list, p);
  return p;
}

/**
 * The clients matching `query` on the lists `stream` searches, active first and then by name. A list that can't be
 * read is named in `unavailable`; the other list's matches still come back. Never picks for the caller.
 */
export async function searchClients(
  io: ClientDirectoryIO,
  stream: string | null | undefined,
  query: string,
): Promise<ClientSearchResult> {
  const lists = listsForStream(stream);
  const loaded = await Promise.all(lists.map(async (list) => ({ list, load: await loadList(io, list) })));
  const matches: ClientEntry[] = [];
  const unavailable: ClientSearchResult['unavailable'] = [];
  for (const { list, load } of loaded) {
    if (load.ok) matches.push(...load.entries.filter((e) => matchesQuery(e, query)));
    else unavailable.push({ list, reason: load.reason });
  }
  return { matches: sortEntries(matches), unavailable };
}
